import { createContext, useContext, useEffect, useState } from "react";

export const translations = {
  en: {
    nav: { about: "About", industries: "Industries", services: "Services", process: "Process", reach: "Global Reach", contact: "Contact", insights: "Insights", quote: "Request a Quote" },
    hero: {
      eyebrow: "Sourcing · Procurement · Trading · Supply",
      l1: "Connecting Global Buyers", l2a: "With", l2b: "Reliable", l3: "Supply Solutions.",
      sub: "Norex International Trading Ltd. is an international sourcing, procurement, trading, import, export, distribution, and supply company serving clients across energy, minerals, healthcare, industrial products, machinery, agricultural commodities, textiles, and strategic raw materials.",
      cta1: "Request a Quote", cta2: "Become a Supplier",
      strip: "Abuja — Dubai — Mumbai — Shanghai — Rotterdam — New York", scroll: "Scroll",
    },
    marquee: ["Global Sourcing", "Procurement", "Import & Export", "Commodity Trading", "Bulk Supply", "Distribution", "Logistics Coordination", "Trade Facilitation"],
    about: {
      eyebrow: "About Norex",
      title: "A trading partner built on trust, not transactions.",
      chapters: [
        { title: "Who We Are", text: "Norex International Trading Ltd. is a diversified international trading company focused on connecting reliable producers, manufacturers, suppliers, distributors, and qualified buyers across global markets. We support businesses through sourcing, procurement, supply coordination, import and export operations, product distribution, and international trade facilitation across multiple industries." },
        { title: "Our Mission", text: "To build long-term business relationships through professionalism, transparency, quality, and efficient supply solutions." },
        { title: "Our Vision", text: "To become a trusted international trading and procurement partner connecting businesses worldwide." },
      ],
      valuesTitle: "Core Values",
      values: ["Integrity", "Reliability", "Transparency", "Quality", "Efficiency", "Long-Term Partnerships"],
      officeLabel: "Head Office", officeValue: "Lugbe, Abuja — Nigeria",
    },
    industries: {
      eyebrow: "Our Industries",
      title: "Eight sectors. One reliable supply chain.",
      sub: "Multi-industry sourcing expertise across the commodities and products that move the world.",
      list: [
        { name: "Energy & Oil Products", items: ["Crude Oil", "Petroleum Products", "LNG", "LPG", "Petrochemicals"] },
        { name: "Minerals & Mining", items: ["Iron Ore", "Gold", "Manganese", "Copper", "Other Minerals"] },
        { name: "Healthcare & Pharmaceuticals", items: ["Medicines", "Medical Consumables", "Medical Devices", "Laboratory Equipment", "Pharma Raw Materials"] },
        { name: "Industrial Machinery & Equipment", items: ["Heavy Machinery", "Plant Equipment", "Spare Parts"] },
        { name: "Agricultural Commodities", items: ["Grains", "Cash Crops", "Processed Foods"] },
        { name: "Textiles & Garments", items: ["Fabrics", "Apparel", "Uniforms"] },
        { name: "Construction Materials", items: ["Cement", "Steel", "Finishing Materials"] },
        { name: "General Import & Export", items: ["Consumer Goods", "Industrial Products", "Strategic Raw Materials"] },
      ],
    },
    services: {
      eyebrow: "Our Services",
      title: "Full-spectrum trade services, under one roof.",
      sub: "From first enquiry to final delivery — a single accountable partner.",
      list: [
        { title: "Global Sourcing", desc: "The right products at the right terms, from qualified origins worldwide." },
        { title: "Procurement Services", desc: "End-to-end purchasing support for organizations and projects." },
        { title: "Supplier Identification", desc: "Vetted producers and manufacturers matched to your specifications." },
        { title: "Buyer–Supplier Matching", desc: "Qualified buyers connected with reliable, verified suppliers." },
        { title: "Import & Export Operations", desc: "Compliant cross-border movement of goods, handled end to end." },
        { title: "Commodity Trading", desc: "Structured trading across energy, minerals, and agricultural markets." },
        { title: "Bulk Supply Solutions", desc: "Volume supply programs built around your recurring demand." },
        { title: "Distribution Services", desc: "Product moved to market through the right channels." },
        { title: "Logistics Coordination", desc: "Freight, shipping, and delivery orchestrated across borders." },
        { title: "Market Research", desc: "Pricing, availability, and market intelligence before you commit." },
        { title: "International Trade Facilitation", desc: "De-risking transactions between parties, banks, and borders." },
        { title: "Documentation Support", desc: "Contracts, certificates, and trade paperwork prepared correctly." },
      ],
    },
    process: {
      eyebrow: "How We Work",
      title: "Six disciplined steps, from enquiry to delivery.",
      step: "Step",
      list: [
        { title: "Understand Buyer Requirements", desc: "We start with your exact specifications, quantities, and delivery terms." },
        { title: "Identify Suitable Suppliers", desc: "Our network is screened for producers matching your product and volume." },
        { title: "Verify Specifications", desc: "Product specs, certifications, and capacity are confirmed before offers." },
        { title: "Commercial Coordination", desc: "Pricing, terms, and contracts aligned between buyer and supplier." },
        { title: "Shipping & Logistics Support", desc: "Freight, documentation, and customs coordination across the route." },
        { title: "Delivery & Follow-up", desc: "Through to final delivery — and into the next order cycle." },
      ],
    },
    why: {
      eyebrow: "Why Choose Norex",
      title: "The partner that stays accountable after the contract is signed.",
      body: "International trade fails on details — missed specifications, slow communication, unreliable counterparties. Norex exists to remove those failures. We work as an extension of your team, with a quality-first approach at every step of the supply chain.",
      points: ["Global Supplier Network", "Multi-Industry Expertise", "Flexible Supply Solutions", "Quality-Focused Approach", "Efficient Communication", "International Trade Experience", "Long-Term Business Relationships"],
    },
    buysup: {
      buyers: { label: "For Buyers", title: "Looking for reliable supply sources?", intro: "Send us your:", items: ["Product details", "Specifications", "Quantity", "Delivery destination"], note: "Our sourcing team will work to identify suitable supply options for your requirement.", cta: "Submit RFQ" },
      suppliers: { label: "For Suppliers", title: "Expand your reach into international markets.", body: "Manufacturers, miners, producers, exporters, distributors, and suppliers are welcome to partner with Norex International Trading Ltd. to expand their international market reach.", cta: "Become a Supplier" },
    },
    rfq: {
      eyebrow: "Request for Quotation",
      title: "Tell us what you need. We'll find who can supply it.",
      labels: { fullName: "Full Name *", company: "Company Name *", email: "Email Address *", phone: "Phone Number", country: "Country *", product: "Product Required *", quantity: "Quantity", destination: "Destination Port / Country", terms: "Preferred Delivery Terms", specs: "Product Specifications", additional: "Additional Information", upload: "Upload Documents (optional, max 10MB)" },
      placeholders: { fullName: "Your full name", company: "Your company", email: "you@company.com", phone: "+234 ...", country: "Your country", product: "e.g. AGO Diesel, Iron Ore", quantity: "e.g. 50,000 MT / month", destination: "e.g. Apapa Port, Lagos", terms: "Select Incoterm", specs: "Grade, standard, packaging, certifications...", additional: "Timeline, target price, anything else we should know...", upload: "Specs sheet, LOI, or product documents" },
      terms: ["EXW", "FOB", "CFR", "CIF", "DAP", "DDP", "Other / To be discussed"],
      submit: "Submit RFQ", submitting: "Submitting...",
      success: "RFQ submitted — our sourcing team will contact you shortly.",
      error: "Submission failed. Please try again.",
      fileTooBig: "File exceeds the 10MB limit",
      nextTitle: "What happens next",
      nextSteps: ["Our sourcing team reviews your requirement within 24–48 hours.", "We identify and verify suitable suppliers from our network.", "You receive supply options with pricing and terms."],
      resp1: "Response within 24–48 hours", resp2: "Your enquiry stays confidential",
      whatsapp: "Prefer to chat? WhatsApp +234 906 804 3369",
    },
    supplier: {
      eyebrow: "Partner With Norex",
      title: "Your product. Our buyers. Global markets.",
      body: "Manufacturers, miners, producers, exporters, distributors, and suppliers are welcome to partner with Norex International Trading Ltd. to expand their international market reach.",
      bullets: ["Access to qualified international buyers", "Structured, transparent trade processes", "Support with export documentation and logistics", "Long-term offtake and distribution relationships"],
      labels: { company: "Company Name *", contact: "Contact Person *", email: "Email Address *", phone: "Phone Number", country: "Country *", products: "Products / Categories Offered *", message: "Message" },
      placeholders: { company: "Your company", contact: "Full name", email: "you@company.com", phone: "+234 ...", country: "Country of operation", products: "e.g. Crude oil, sesame seeds, medical consumables", message: "Capacity, certifications, export experience..." },
      submit: "Become a Supplier", submitting: "Submitting...",
      success: "Application received — our partnerships team will be in touch.",
      error: "Submission failed. Please try again.",
    },
    testimonials: {
      eyebrow: "Client Voices",
      title: "Trusted across borders and industries.",
      partnersLabel: "Our clients & trade partners",
      verifiedBadge: "Norex Verified",
      fallback: [
        { quote: "Norex coordinated our petroleum supply from specification check to port discharge — without a single delay.", name: "Adewale Johnson", org: "Sahel Energy Ltd — Nigeria" },
        { quote: "Their supplier verification saved us months of due diligence. We now source medical consumables through one reliable channel.", name: "Fatima Al-Rashid", org: "GulfMed Healthcare Group — UAE" },
        { quote: "Transparent communication at every step. Exactly how commodity trading should work.", name: "Daniel Mwangi", org: "EastAfrica Agro Exports — Kenya" },
        { quote: "From LOI to delivery, the documentation support was flawless.", name: "Sophie Laurent", org: "NordIndustrial SARL — France" },
        { quote: "A reliable bridge between our mine and qualified buyers we could never reach directly.", name: "Thabo Nkosi", org: "Copperbelt Mining Co — South Africa" },
      ],
      partnerNames: ["SAHEL ENERGY", "GOLDCREST MINES", "MEDIPHARM GROUP", "ATLAS AGRO", "NORDWEG TRADE", "KANO TEXTILES"],
    },
    reach: {
      eyebrow: "Global Reach",
      title: "Trade routes across five regions.",
      sub: "Connecting producers, suppliers, manufacturers, and buyers across international markets.",
      regions: ["Africa", "Middle East", "Asia", "Europe", "Americas"],
    },
    contact: {
      eyebrow: "Contact",
      title: "Let's talk trade.",
      officeLabel: "Head Office", phoneLabel: "Phone", emailLabel: "Email",
      emailBtn: "Email Us", whatsappBtn: "Chat on WhatsApp", quoteBtn: "Request a Quote",
      cardEyebrow: "Norex International Trading Ltd.",
      cardTitle: "One enquiry away from your next supply line.",
    },
    footer: {
      blurb: "An international sourcing, procurement, trading, import, export, distribution, and supply company connecting reliable producers and qualified buyers across global markets.",
      quickTitle: "Quick Links", legalTitle: "Legal", officeTitle: "Head Office",
      links: { home: "Home", about: "About", industries: "Industries", services: "Services", rfq: "For Buyers", suppliers: "For Suppliers", contact: "Contact", insights: "Insights" },
      legal: { privacy: "Privacy Policy", terms: "Terms & Conditions", disclaimer: "Disclaimer" },
      copyright: "© 2026 Norex International Trading Ltd. All Rights Reserved.",
    },
    commodity: {
      back: "Back to Industries", overview: "What Norex Offers", specs: "Typical Specifications & Trade Details",
      regions: "Key Sourcing Regions", formTitle: "Request a Quote",
      formSub: "Share your volume, destination, and terms — our sourcing team responds within 24–48 hours.",
      product: "Commodity", notFound: "Commodity not found.", msgLabel: "Message / Specifications",
      msgPlaceholder: "Grade, standard, packaging, certifications, timeline...",
    },
    insights: {
      eyebrow: "Trade Insights",
      title: "Notes on global trade and commodities.",
      sub: "Market observations and sourcing intelligence from the Norex trading desk.",
      readMore: "Read article", back: "Back to insights", empty: "No insights published yet — check back soon.",
    },
    float: { tooltip: "Chat with us on WhatsApp" },
  },
  fr: {
    nav: { about: "À propos", industries: "Secteurs", services: "Services", process: "Processus", reach: "Présence Mondiale", contact: "Contact", insights: "Analyses", quote: "Demander un Devis" },
    hero: {
      eyebrow: "Sourcing · Achats · Négoce · Distribution",
      l1: "Connecter les Acheteurs du Monde", l2a: "À des Solutions", l2b: "Fiables", l3: "d’Approvisionnement.",
      sub: "Norex International Trading Ltd. est une société internationale de sourcing, d’approvisionnement, de négoce, d’import-export, de distribution et de supply, au service de clients dans l’énergie, les minerais, la santé, les produits industriels, les machines, les commodités agricoles, les textiles et les matières premières stratégiques.",
      cta1: "Demander un Devis", cta2: "Devenir Fournisseur",
      strip: "Abuja — Dubaï — Mumbai — Shanghai — Rotterdam — New York", scroll: "Défiler",
    },
    marquee: ["Sourcing Mondial", "Approvisionnement", "Import & Export", "Négoce de Commodités", "Supply en Vrac", "Distribution", "Coordination Logistique", "Facilitation du Commerce"],
    about: {
      eyebrow: "À Propos de Norex",
      title: "Un partenaire commercial fondé sur la confiance, pas sur les transactions.",
      chapters: [
        { title: "Qui Sommes-Nous", text: "Norex International Trading Ltd. est une société de négoce international diversifiée qui connecte des producteurs, fabricants, fournisseurs, distributeurs et acheteurs qualifiés fiables à travers les marchés mondiaux. Nous accompagnons les entreprises dans le sourcing, l’approvisionnement, la coordination des approvisionnements, les opérations d’import-export, la distribution de produits et la facilitation du commerce international dans de nombreux secteurs." },
        { title: "Notre Mission", text: "Bâtir des relations commerciales durables grâce au professionnalisme, à la transparence, à la qualité et à des solutions d’approvisionnement efficaces." },
        { title: "Notre Vision", text: "Devenir un partenaire de confiance en négoce international et en approvisionnement, connectant les entreprises du monde entier." },
      ],
      valuesTitle: "Nos Valeurs",
      values: ["Intégrité", "Fiabilité", "Transparence", "Qualité", "Efficacité", "Partenariats Durables"],
      officeLabel: "Siège", officeValue: "Lugbe, Abuja — Nigéria",
    },
    industries: {
      eyebrow: "Nos Secteurs",
      title: "Huit secteurs. Une seule chaîne d’approvisionnement fiable.",
      sub: "Une expertise multi-sectorielle du sourcing sur les produits qui font bouger le monde.",
      list: [
        { name: "Énergie & Produits Pétroliers", items: ["Pétrole Brut", "Produits Pétroliers", "GNL", "GPL", "Produits Pétrochimiques"] },
        { name: "Minerais & Mines", items: ["Minerai de Fer", "Or", "Manganèse", "Cuivre", "Autres Minerais"] },
        { name: "Santé & Pharmaceutique", items: ["Médicaments", "Consommables Médicaux", "Dispositifs Médicaux", "Équipements de Laboratoire", "Matières Premières Pharma"] },
        { name: "Machines & Équipements Industriels", items: ["Machines Lourdes", "Équipements d’Usine", "Pièces Détachées"] },
        { name: "Commodités Agricoles", items: ["Céréales", "Cultures de Rente", "Produits Transformés"] },
        { name: "Textiles & Vêtements", items: ["Tissus", "Vêtements", "Uniformes"] },
        { name: "Matériaux de Construction", items: ["Ciment", "Acier", "Matériaux de Finition"] },
        { name: "Import & Export Général", items: ["Biens de Consommation", "Produits Industriels", "Matières Premières Stratégiques"] },
      ],
    },
    services: {
      eyebrow: "Nos Services",
      title: "Une gamme complète de services commerciaux, sous un même toit.",
      sub: "De la première demande à la livraison finale — un interlocuteur unique et responsable.",
      list: [
        { title: "Sourcing Mondial", desc: "Les bons produits aux bonnes conditions, depuis des origines qualifiées dans le monde entier." },
        { title: "Services d’Approvisionnement", desc: "Un accompagnement complet de vos achats, pour organisations et projets." },
        { title: "Identification de Fournisseurs", desc: "Des producteurs et fabricants vérifiés, correspondant à vos spécifications." },
        { title: "Mise en Relation Acheteurs–Fournisseurs", desc: "Des acheteurs qualifiés connectés à des fournisseurs fiables et vérifiés." },
        { title: "Opérations Import & Export", desc: "Circulation transfrontalière de marchandises en conformité, de bout en bout." },
        { title: "Négoce de Commodités", desc: "Un négoce structuré sur les marchés de l’énergie, des minerais et de l’agriculture." },
        { title: "Solutions d’Approvisionnement en Vrac", desc: "Des programmes d’approvisionnement en volume adaptés à votre demande récurrente." },
        { title: "Services de Distribution", desc: "Vos produits acheminés vers le marché par les bons canaux." },
        { title: "Coordination Logistique", desc: "Fret, expédition et livraison orchestrés à travers les frontières." },
        { title: "Études de Marché", desc: "Prix, disponibilités et veille de marché avant chaque engagement." },
        { title: "Facilitation du Commerce International", desc: "Sécuriser les transactions entre parties, banques et frontières." },
        { title: "Assistance Documentaire", desc: "Contrats, certificats et documents commerciaux préparés avec rigueur." },
      ],
    },
    process: {
      eyebrow: "Notre Méthode",
      title: "Six étapes rigoureuses, de la demande à la livraison.",
      step: "Étape",
      list: [
        { title: "Comprendre les Besoins de l’Acheteur", desc: "Nous partons de vos spécifications exactes, quantités et conditions de livraison." },
        { title: "Identifier les Fournisseurs Adaptés", desc: "Notre réseau est scruté pour trouver des producteurs correspondant à votre produit et volume." },
        { title: "Vérifier les Spécifications", desc: "Spécifications produit, certifications et capacités sont confirmées avant toute offre." },
        { title: "Coordination Commerciale", desc: "Prix, conditions et contrats alignés entre acheteur et fournisseur." },
        { title: "Expédition & Support Logistique", desc: "Fret, documentation et coordination douanière sur toute la route." },
        { title: "Livraison & Suivi", desc: "Jusqu’à la livraison finale — et vers le cycle de commande suivant." },
      ],
    },
    why: {
      eyebrow: "Pourquoi Choisir Norex",
      title: "Le partenaire qui reste engagé après la signature du contrat.",
      body: "Le commerce international échoue sur les détails — spécifications manquées, communication lente, partenaires peu fiables. Norex existe pour éliminer ces échecs. Nous travaillons comme une extension de votre équipe, avec une approche axée sur la qualité à chaque étape de la chaîne d’approvisionnement.",
      points: ["Réseau Mondial de Fournisseurs", "Expertise Multi-Sectorielle", "Solutions d’Approvisionnement Flexibles", "Approche Axée sur la Qualité", "Communication Efficace", "Expérience du Commerce International", "Relations Commerciales Durables"],
    },
    buysup: {
      buyers: { label: "Pour les Acheteurs", title: "Vous cherchez des sources d’approvisionnement fiables ?", intro: "Envoyez-nous vos :", items: ["Détails du produit", "Spécifications", "Quantité", "Destination de livraison"], note: "Notre équipe sourcing identifiera les options d’approvisionnement adaptées à votre besoin.", cta: "Soumettre une RFQ" },
      suppliers: { label: "Pour les Fournisseurs", title: "Étendez votre présence sur les marchés internationaux.", body: "Fabricants, mineurs, producteurs, exportateurs, distributeurs et fournisseurs sont invités à s’associer à Norex International Trading Ltd. pour étendre leur portée sur les marchés internationaux.", cta: "Devenir Fournisseur" },
    },
    rfq: {
      eyebrow: "Demande de Cotation",
      title: "Dites-nous ce qu’il vous faut. Nous trouverons qui peut le fournir.",
      labels: { fullName: "Nom Complet *", company: "Nom de l’Entreprise *", email: "Adresse Email *", phone: "Numéro de Téléphone", country: "Pays *", product: "Produit Recherché *", quantity: "Quantité", destination: "Port / Pays de Destination", terms: "Conditions de Livraison Préférées", specs: "Spécifications du Produit", additional: "Informations Complémentaires", upload: "Joindre des Documents (optionnel, 10 Mo max)" },
      placeholders: { fullName: "Votre nom complet", company: "Votre entreprise", email: "vous@entreprise.com", phone: "+234 ...", country: "Votre pays", product: "ex. Diesel AGO, Minerai de Fer", quantity: "ex. 50 000 TM / mois", destination: "ex. Port d’Apapa, Lagos", terms: "Sélectionner un Incoterm", specs: "Grade, norme, emballage, certifications...", additional: "Délais, prix cible, toute autre information utile...", upload: "Fiche technique, LOI ou documents produit" },
      terms: ["EXW", "FOB", "CFR", "CIF", "DAP", "DDP", "Autre / À discuter"],
      submit: "Soumettre la RFQ", submitting: "Envoi en cours...",
      success: "RFQ envoyée — notre équipe sourcing vous contactera sous peu.",
      error: "Échec de l’envoi. Veuillez réessayer.",
      fileTooBig: "Le fichier dépasse la limite de 10 Mo",
      nextTitle: "Et ensuite ?",
      nextSteps: ["Notre équipe sourcing étudie votre demande sous 24 à 48 heures.", "Nous identifions et vérifions les fournisseurs adaptés dans notre réseau.", "Vous recevez des options d’approvisionnement avec prix et conditions."],
      resp1: "Réponse sous 24 à 48 heures", resp2: "Votre demande reste confidentielle",
      whatsapp: "Vous préférez discuter ? WhatsApp +234 906 804 3369",
    },
    supplier: {
      eyebrow: "Partenariat avec Norex",
      title: "Votre produit. Nos acheteurs. Les marchés mondiaux.",
      body: "Fabricants, mineurs, producteurs, exportateurs, distributeurs et fournisseurs sont invités à s’associer à Norex International Trading Ltd. pour étendre leur portée sur les marchés internationaux.",
      bullets: ["Accès à des acheteurs internationaux qualifiés", "Processus commerciaux structurés et transparents", "Assistance pour la documentation export et la logistique", "Relations d’offtake et de distribution durables"],
      labels: { company: "Nom de l’Entreprise *", contact: "Personne de Contact *", email: "Adresse Email *", phone: "Numéro de Téléphone", country: "Pays *", products: "Produits / Catégories Proposés *", message: "Message" },
      placeholders: { company: "Votre entreprise", contact: "Nom complet", email: "vous@entreprise.com", phone: "+234 ...", country: "Pays d’opération", products: "ex. pétrole brut, graines de sésame, consommables médicaux", message: "Capacités, certifications, expérience export..." },
      submit: "Devenir Fournisseur", submitting: "Envoi en cours...",
      success: "Candidature reçue — notre équipe partenariats vous contactera.",
      error: "Échec de l’envoi. Veuillez réessayer.",
    },
    testimonials: {
      eyebrow: "Témoignages Clients",
      title: "Une confiance au-delà des frontières et des secteurs.",
      partnersLabel: "Nos clients & partenaires commerciaux",
      verifiedBadge: "Norex Vérifié",
      fallback: [
        { quote: "Norex a coordonné notre approvisionnement pétrolier, du contrôle des spécifications à la décharge au port — sans le moindre retard.", name: "Adewale Johnson", org: "Sahel Energy Ltd — Nigéria" },
        { quote: "Leur vérification des fournisseurs nous a fait gagner des mois de due diligence. Nous sourçons désormais nos consommables médicaux via un canal unique et fiable.", name: "Fatima Al-Rashid", org: "GulfMed Healthcare Group — Émirats" },
        { quote: "Une communication transparente à chaque étape. Exactement ainsi que le négoce devrait fonctionner.", name: "Daniel Mwangi", org: "EastAfrica Agro Exports — Kenya" },
        { quote: "De la LOI à la livraison, l’assistance documentaire a été irréprochable.", name: "Sophie Laurent", org: "NordIndustrial SARL — France" },
        { quote: "Un pont fiable entre notre mine et des acheteurs qualifiés que nous ne pouvions atteindre directement.", name: "Thabo Nkosi", org: "Copperbelt Mining Co — Afrique du Sud" },
      ],
      partnerNames: ["SAHEL ENERGY", "GOLDCREST MINES", "MEDIPHARM GROUP", "ATLAS AGRO", "NORDWEG TRADE", "KANO TEXTILES"],
    },
    reach: {
      eyebrow: "Présence Mondiale",
      title: "Des routes commerciales sur cinq régions.",
      sub: "Connecter producteurs, fournisseurs, fabricants et acheteurs à travers les marchés internationaux.",
      regions: ["Afrique", "Moyen-Orient", "Asie", "Europe", "Amériques"],
    },
    contact: {
      eyebrow: "Contact",
      title: "Parlons commerce.",
      officeLabel: "Siège", phoneLabel: "Téléphone", emailLabel: "Email",
      emailBtn: "Écrivez-nous", whatsappBtn: "Discuter sur WhatsApp", quoteBtn: "Demander un Devis",
      cardEyebrow: "Norex International Trading Ltd.",
      cardTitle: "Votre prochaine ligne d’approvisionnement commence par un message.",
    },
    footer: {
      blurb: "Une société internationale de sourcing, d’approvisionnement, de négoce, d’import-export, de distribution et de supply, connectant producteurs fiables et acheteurs qualifiés à travers les marchés mondiaux.",
      quickTitle: "Liens Rapides", legalTitle: "Mentions Légales", officeTitle: "Siège",
      links: { home: "Accueil", about: "À propos", industries: "Secteurs", services: "Services", rfq: "Pour les Acheteurs", suppliers: "Pour les Fournisseurs", contact: "Contact", insights: "Analyses" },
      legal: { privacy: "Politique de Confidentialité", terms: "Conditions Générales", disclaimer: "Avertissement" },
      copyright: "© 2026 Norex International Trading Ltd. Tous droits réservés.",
    },
    commodity: {
      back: "Retour aux Secteurs", overview: "Ce que Norex Propose", specs: "Spécifications & Détails Commerciaux Typiques",
      regions: "Principales Régions de Sourcing", formTitle: "Demander un Devis",
      formSub: "Indiquez volume, destination et conditions — notre équipe sourcing répond sous 24 à 48 heures.",
      product: "Produit", notFound: "Produit introuvable.", msgLabel: "Message / Spécifications",
      msgPlaceholder: "Grade, norme, emballage, certifications, délais...",
    },
    insights: {
      eyebrow: "Analyses des Marchés",
      title: "Regards sur le commerce mondial et les commodités.",
      sub: "Observations de marché et intelligence sourcing du bureau de négoce Norex.",
      readMore: "Lire l’article", back: "Retour aux analyses", empty: "Aucune analyse publiée pour le moment — revenez bientôt.",
    },
    float: { tooltip: "Discutez avec nous sur WhatsApp" },
  },
  ar: {
    nav: { about: "عن الشركة", industries: "القطاعات", services: "الخدمات", process: "منهجية العمل", reach: "الانتشار العالمي", contact: "اتصل بنا", insights: "الرؤى", quote: "اطلب عرض سعر" },
    hero: {
      eyebrow: "توريد · مشتريات · تجارة · توزيع",
      l1: "نربط المشترين حول العالم", l2a: "بحلول", l2b: "موثوقة", l3: "للتوريد.",
      sub: "نوركس إنترناشونال تريدينغ المحدودة هي شركة دولية للتوريد والمشتريات والتجارة والاستيراد والتصدير والتوزيع والإمداد، تخدم عملاء في قطاعات الطاقة والمعادن والرعاية الصحية والمنتجات الصناعية والآلات والسلع الزراعية والمنسوجات والمواد الخام الاستراتيجية.",
      cta1: "اطلب عرض سعر", cta2: "انضم كمورّد",
      strip: "أبوجا — دبي — مومباي — شنغهاي — روتردام — نيويورك", scroll: "مرّر",
    },
    marquee: ["التوريد العالمي", "المشتريات", "الاستيراد والتصدير", "تجارة السلع", "الإمداد بالجملة", "التوزيع", "تنسيق الخدمات اللوجستية", "تيسير التجارة"],
    about: {
      eyebrow: "عن نوركس",
      title: "شريك تجاري مبني على الثقة، لا على المعاملات.",
      chapters: [
        { title: "من نحن", text: "نوركس إنترناشونال تريدينغ المحدودة هي شركة تجارة دولية متنوعة تركز على ربط المنتجين والمصنعين والمورّدين والموزعين والمشترين المؤهلين الموثوقين عبر الأسواق العالمية. ندعم الشركات من خلال التوريد والمشتريات وتنسيق الإمداد وعمليات الاستيراد والتصدير وتوزيع المنتجات وتيسير التجارة الدولية عبر قطاعات متعددة." },
        { title: "مهمتنا", text: "بناء علاقات تجارية طويلة الأمد من خلال الاحترافية والشفافية والجودة وحلول التوريد الفعالة." },
        { title: "رؤيتنا", text: "أن نصبح شريكاً موثوقاً في التجارة الدولية والمشتريات، نربط الشركات حول العالم." },
      ],
      valuesTitle: "قيمنا",
      values: ["النزاهة", "الموثوقية", "الشفافية", "الجودة", "الكفاءة", "شراكات طويلة الأمد"],
      officeLabel: "المقر الرئيسي", officeValue: "لوبي، أبوجا — نيجيريا",
    },
    industries: {
      eyebrow: "قطاعاتنا",
      title: "ثمانية قطاعات. سلسلة توريد واحدة موثوقة.",
      sub: "خبرة توريد متعددة القطاعات عبر السلع والمنتجات التي تحرك العالم.",
      list: [
        { name: "الطاقة والمنتجات النفطية", items: ["نفط خام", "منتجات بترولية", "الغاز الطبيعي المسال", "غاز البترول المسال", "البتروكيماويات"] },
        { name: "المعادن والتعدين", items: ["خام الحديد", "الذهب", "المنغنيز", "النحاس", "معادن أخرى"] },
        { name: "الرعاية الصحية والأدوية", items: ["أدوية", "مستهلكات طبية", "أجهزة طبية", "معدات مختبرات", "مواد خام صيدلانية"] },
        { name: "الآلات والمعدات الصناعية", items: ["آلات ثقيلة", "معدات مصانع", "قطع غيار"] },
        { name: "السلع الزراعية", items: ["حبوب", "محاصيل نقدية", "أغذية مصنعة"] },
        { name: "المنسوجات والملابس", items: ["أقمشة", "ملابس", "زي موحد"] },
        { name: "مواد البناء", items: ["أسمنت", "حديد", "مواد تشطيب"] },
        { name: "الاستيراد والتصدير العام", items: ["سلع استهلاكية", "منتجات صناعية", "مواد خام استراتيجية"] },
      ],
    },
    services: {
      eyebrow: "خدماتنا",
      title: "خدمات تجارية شاملة تحت سقف واحد.",
      sub: "من أول استفسار حتى التسليم النهائي — شريك واحد مسؤول.",
      list: [
        { title: "التوريد العالمي", desc: "المنتجات المناسبة بالشروط المناسبة، من مصادر مؤهلة حول العالم." },
        { title: "خدمات المشتريات", desc: "دعم شامل للشراء للمؤسسات والمشاريع." },
        { title: "تحديد المورّدين", desc: "منتجون ومصنعون موثقون يطابقون مواصفاتك." },
        { title: "الربط بين المشترين والمورّدين", desc: "ربط المشترين المؤهلين بمورّدين موثوقين ومتحقق منهم." },
        { title: "عمليات الاستيراد والتصدير", desc: "نقل البضائع عبر الحدود بامتثال كامل، من البداية إلى النهاية." },
        { title: "تجارة السلع", desc: "تجارة منظمة في أسواق الطاقة والمعادن والزراعة." },
        { title: "حلول الإمداد بالجملة", desc: "برامج توريد بالجملة مبنية حول طلبك المتكرر." },
        { title: "خدمات التوزيع", desc: "توصيل المنتجات إلى السوق عبر القنوات المناسبة." },
        { title: "تنسيق الخدمات اللوجستية", desc: "الشحن والنقل والتسليم منسق عبر الحدود." },
        { title: "أبحاث السوق", desc: "الأسعار والتوفر ومعلومات السوق قبل الالتزام." },
        { title: "تيسير التجارة الدولية", desc: "تقليل مخاطر المعاملات بين الأطراف والبنوك والحدود." },
        { title: "الدعم التوثيقي", desc: "العقود والشهادات والأوراق التجارية بإعداد دقيق." },
      ],
    },
    process: {
      eyebrow: "منهجية عملنا",
      title: "ست خطوات منضبطة، من الاستفسار إلى التسليم.",
      step: "الخطوة",
      list: [
        { title: "فهم متطلبات المشتري", desc: "نبدأ بمواصفاتك الدقيقة والكميات وشروط التسليم." },
        { title: "تحديد المورّدين المناسبين", desc: "نفحص شبكتنا لإيجاد منتجين يطابقون منتجك وحجمه." },
        { title: "التحقق من المواصفات", desc: "يتم تأكيد مواصفات المنتج والشهادات والقدرة قبل العروض." },
        { title: "التنسيق التجاري", desc: "مواءمة الأسعار والشروط والعقود بين المشتري والمورّد." },
        { title: "الشحن والدعم اللوجستي", desc: "الشحن والتوثيق والتنسيق الجمركي على كامل المسار." },
        { title: "التسليم والمتابعة", desc: "حتى التسليم النهائي — ونحو دورة الطلب التالية." },
      ],
    },
    why: {
      eyebrow: "لماذا تختار نوركس",
      title: "الشريك الذي يبقى مسؤولاً بعد توقيع العقد.",
      body: "تفشل التجارة الدولية في التفاصيل — مواصفات مفقودة، تواصل بطيء، أطراف غير موثوقة. توجد نوركس لإزالة هذه الإخفاقات. نعمل كامتداد لفريقك، بنهج يضع الجودة أولاً في كل خطوة من سلسلة التوريد.",
      points: ["شبكة مورّدين عالمية", "خبرة متعددة القطاعات", "حلول توريد مرنة", "نهج يركز على الجودة", "تواصل فعّال", "خبرة في التجارة الدولية", "علاقات تجارية طويلة الأمد"],
    },
    buysup: {
      buyers: { label: "للمشترين", title: "هل تبحث عن مصادر توريد موثوقة؟", intro: "أرسل لنا:", items: ["تفاصيل المنتج", "المواصفات", "الكمية", "وجهة التسليم"], note: "سيعمل فريق التوريد لدينا على تحديد خيارات التوريد المناسبة لاحتياجك.", cta: "قدّم طلب عرض سعر" },
      suppliers: { label: "للمورّدين", title: "وسّع حضورك في الأسواق الدولية.", body: "نرحب بالمصنعين والمنجمين والمنتجين والمصدّرين والموزعين والمورّدين للشراكة مع نوركس إنترناشونال تريدينغ المحدودة لتوسيع حضورهم في الأسواق الدولية.", cta: "انضم كمورّد" },
    },
    rfq: {
      eyebrow: "طلب عرض سعر",
      title: "أخبرنا بما تحتاج. سنجد من يمكنه توريده.",
      labels: { fullName: "الاسم الكامل *", company: "اسم الشركة *", email: "البريد الإلكتروني *", phone: "رقم الهاتف", country: "الدولة *", product: "المنتج المطلوب *", quantity: "الكمية", destination: "ميناء / دولة الوجهة", terms: "شروط التسليم المفضلة", specs: "مواصفات المنتج", additional: "معلومات إضافية", upload: "إرفاق مستندات (اختياري، 10 ميغابايت كحد أقصى)" },
      placeholders: { fullName: "اسمك الكامل", company: "شركتك", email: "you@company.com", phone: "+234 ...", country: "دولتك", product: "مثال: ديزل AGO، خام حديد", quantity: "مثال: 50,000 طن متري / شهر", destination: "مثال: ميناء أبابا، لاغوس", terms: "اختر شرط التسليم", specs: "الدرجة، المعيار، التغليف، الشهادات...", additional: "الجدول الزمني، السعر المستهدف، أي معلومات أخرى...", upload: "ورقة المواصفات، خطاب النوايا أو مستندات المنتج" },
      terms: ["EXW", "FOB", "CFR", "CIF", "DAP", "DDP", "أخرى / للنقاش"],
      submit: "إرسال الطلب", submitting: "جارٍ الإرسال...",
      success: "تم إرسال الطلب — سيتواصل معك فريق التوريد قريباً.",
      error: "فشل الإرسال. يرجى المحاولة مرة أخرى.",
      fileTooBig: "الملف يتجاوز حد 10 ميغابايت",
      nextTitle: "ماذا بعد؟",
      nextSteps: ["يراجع فريق التوريد طلبك خلال 24–48 ساعة.", "نحدد ونتحقق من المورّدين المناسبين في شبكتنا.", "تستلم خيارات التوريد مع الأسعار والشروط."],
      resp1: "رد خلال 24–48 ساعة", resp2: "يبقى استفسارك سرياً",
      whatsapp: "تفضّل المحادثة؟ واتساب +234 906 804 3369",
    },
    supplier: {
      eyebrow: "شراكة مع نوركس",
      title: "منتجك. مشترونا. الأسواق العالمية.",
      body: "نرحب بالمصنعين والمنجمين والمنتجين والمصدّرين والموزعين والمورّدين للشراكة مع نوركس إنترناشونال تريدينغ المحدودة لتوسيع حضورهم في الأسواق الدولية.",
      bullets: ["وصول إلى مشترين دوليين مؤهلين", "عمليات تجارية منظمة وشفافة", "دعم في وثائق التصدير واللوجستيات", "علاقات شراء وتوزيع طويلة الأمد"],
      labels: { company: "اسم الشركة *", contact: "الشخص المسؤول *", email: "البريد الإلكتروني *", phone: "رقم الهاتف", country: "الدولة *", products: "المنتجات / الفئات المعروضة *", message: "رسالة" },
      placeholders: { company: "شركتك", contact: "الاسم الكامل", email: "you@company.com", phone: "+234 ...", country: "دولة العمل", products: "مثال: نفط خام، بذور السمسم، مستهلكات طبية", message: "القدرات، الشهادات، خبرة التصدير..." },
      submit: "انضم كمورّد", submitting: "جارٍ الإرسال...",
      success: "تم استلام الطلب — سيتواصل معك فريق الشراكات.",
      error: "فشل الإرسال. يرجى المحاولة مرة أخرى.",
    },
    testimonials: {
      eyebrow: "آراء العملاء",
      title: "ثقة تتجاوز الحدود والقطاعات.",
      partnersLabel: "عملاؤنا وشركاؤنا التجاريون",
      verifiedBadge: "موثّق من نوركس",
      fallback: [
        { quote: "نسّقت نوركس توريدنا البترولي من فحص المواصفات حتى التفريغ في الميناء — دون أي تأخير.", name: "أديوالي جونسون", org: "Sahel Energy Ltd — نيجيريا" },
        { quote: "وفّر لنا التحقق من المورّدين شهوراً من العناية الواجبة. نورّد الآن المستهلكات الطبية عبر قناة واحدة موثوقة.", name: "فاطمة الراشد", org: "GulfMed Healthcare Group — الإمارات" },
        { quote: "تواصل شفاف في كل خطوة. هكذا يجب أن تعمل تجارة السلع تماماً.", name: "دانيال موانجي", org: "EastAfrica Agro Exports — كينيا" },
        { quote: "من خطاب النوايا حتى التسليم، كان الدعم التوثيقي مثالياً.", name: "صوفي لوران", org: "NordIndustrial SARL — فرنسا" },
        { quote: "جسر موثوق بين منجمنا ومشترين مؤهلين لم نكن لنصل إليهم مباشرة.", name: "ثابو نكوسي", org: "Copperbelt Mining Co — جنوب أفريقيا" },
      ],
      partnerNames: ["SAHEL ENERGY", "GOLDCREST MINES", "MEDIPHARM GROUP", "ATLAS AGRO", "NORDWEG TRADE", "KANO TEXTILES"],
    },
    reach: {
      eyebrow: "الانتشار العالمي",
      title: "طرق تجارية عبر خمس مناطق.",
      sub: "نربط المنتجين والمورّدين والمصنعين والمشترين عبر الأسواق الدولية.",
      regions: ["أفريقيا", "الشرق الأوسط", "آسيا", "أوروبا", "الأمريكيتان"],
    },
    contact: {
      eyebrow: "اتصل بنا",
      title: "لنتحدث في التجارة.",
      officeLabel: "المقر الرئيسي", phoneLabel: "الهاتف", emailLabel: "البريد الإلكتروني",
      emailBtn: "راسلنا عبر البريد", whatsappBtn: "تحدث عبر واتساب", quoteBtn: "اطلب عرض سعر",
      cardEyebrow: "نوركس إنترناشونال تريدينغ المحدودة",
      cardTitle: "استفسار واحد يفصلك عن خط التوريد القادم.",
    },
    footer: {
      blurb: "شركة دولية للتوريد والمشتريات والتجارة والاستيراد والتصدير والتوزيع والإمداد، تربط منتجين موثوقين ومشترين مؤهلين عبر الأسواق العالمية.",
      quickTitle: "روابط سريعة", legalTitle: "قانوني", officeTitle: "المقر الرئيسي",
      links: { home: "الرئيسية", about: "عن الشركة", industries: "القطاعات", services: "الخدمات", rfq: "للمشترين", suppliers: "للمورّدين", contact: "اتصل بنا", insights: "الرؤى" },
      legal: { privacy: "سياسة الخصوصية", terms: "الشروط والأحكام", disclaimer: "إخلاء المسؤولية" },
      copyright: "© 2026 نوركس إنترناشونال تريدينغ المحدودة. جميع الحقوق محفوظة.",
    },
    commodity: {
      back: "العودة إلى القطاعات", overview: "ما تقدمه نوركس", specs: "المواصفات وتفاصيل التجارة النموذجية",
      regions: "أبرز مناطق التوريد", formTitle: "اطلب عرض سعر",
      formSub: "شاركنا الكمية والوجهة والشروط — يرد فريق التوريد خلال 24–48 ساعة.",
      product: "السلعة", notFound: "السلعة غير موجودة.", msgLabel: "رسالة / مواصفات",
      msgPlaceholder: "الدرجة، المعيار، التغليف، الشهادات، الجدول الزمني...",
    },
    insights: {
      eyebrow: "رؤى السوق",
      title: "ملاحظات حول التجارة العالمية والسلع.",
      sub: "ملاحظات السوق وذكاء التوريد من مكتب تجارة نوركس.",
      readMore: "اقرأ المقال", back: "العودة إلى الرؤى", empty: "لا توجد رؤى منشورة بعد — عد قريباً.",
    },
    float: { tooltip: "تحدث معنا عبر واتساب" },
  },
  zh: {
    nav: { about: "关于我们", industries: "行业领域", services: "服务", process: "工作流程", reach: "全球网络", contact: "联系我们", insights: "市场洞察", quote: "获取报价" },
    hero: {
      eyebrow: "采购 · 供应 · 贸易 · 配送",
      l1: "连接全球买家", l2a: "与", l2b: "可靠的", l3: "供应解决方案。",
      sub: "Norex International Trading Ltd. 是一家国际化的采购、供应、贸易、进出口、分销与供应公司，服务于能源、矿产、医疗健康、工业产品、机械设备、农产品、纺织品及战略原材料等领域的客户。",
      cta1: "获取报价", cta2: "成为供应商",
      strip: "阿布贾 — 迪拜 — 孟买 — 上海 — 鹿特丹 — 纽约", scroll: "下滑",
    },
    marquee: ["全球采购", "采购服务", "进出口", "大宗商品贸易", "大宗供应", "分销服务", "物流协调", "贸易促进"],
    about: {
      eyebrow: "关于 Norex",
      title: "以信任而非交易为基础的贸易伙伴。",
      chapters: [
        { title: "我们是谁", text: "Norex International Trading Ltd. 是一家多元化的国际贸易公司，致力于在全球市场连接可靠的生产商、制造商、供应商、分销商与合格买家。我们通过采购、供应协调、进出口运营、产品分销和国际贸易促进，为多个行业的企业提供支持。" },
        { title: "我们的使命", text: "通过专业、透明、优质和高效的供应解决方案，建立长期的商业合作关系。" },
        { title: "我们的愿景", text: "成为值得信赖的国际贸易与采购伙伴，连接全球企业。" },
      ],
      valuesTitle: "核心价值观",
      values: ["诚信", "可靠", "透明", "品质", "高效", "长期合作"],
      officeLabel: "总部", officeValue: "尼日利亚 阿布贾 Lugbe",
    },
    industries: {
      eyebrow: "我们的行业",
      title: "八大行业。一条可靠的供应链。",
      sub: "跨越多行业的大宗商品与产品采购专长，驱动世界运转。",
      list: [
        { name: "能源与石油产品", items: ["原油", "石油产品", "液化天然气", "液化石油气", "石化产品"] },
        { name: "矿产与采矿", items: ["铁矿石", "黄金", "锰", "铜", "其他矿产"] },
        { name: "医疗与制药", items: ["药品", "医疗耗材", "医疗器械", "实验室设备", "药用原料"] },
        { name: "工业机械与设备", items: ["重型机械", "工厂设备", "备件"] },
        { name: "农产品", items: ["谷物", "经济作物", "加工食品"] },
        { name: "纺织与服装", items: ["面料", "服装", "制服"] },
        { name: "建筑材料", items: ["水泥", "钢材", "装饰材料"] },
        { name: "一般进出口", items: ["消费品", "工业产品", "战略原材料"] },
      ],
    },
    services: {
      eyebrow: "我们的服务",
      title: "一站式全方位贸易服务。",
      sub: "从首次询价到最终交付——一个值得信赖的合作伙伴。",
      list: [
        { title: "全球采购", desc: "以合适的条件，从全球合格产地采购合适的产品。" },
        { title: "采购服务", desc: "为机构和项目提供端到端的采购支持。" },
        { title: "供应商识别", desc: "筛选符合您规格的生产商和制造商。" },
        { title: "买卖双方对接", desc: "将合格买家与可靠、经过验证的供应商连接起来。" },
        { title: "进出口运营", desc: "合规的跨境货物运输，全程处理。" },
        { title: "大宗商品贸易", desc: "在能源、矿产和农产品市场开展结构化贸易。" },
        { title: "大宗供应方案", desc: "围绕您的持续需求制定批量供应计划。" },
        { title: "分销服务", desc: "通过合适的渠道将产品送达市场。" },
        { title: "物流协调", desc: "跨境货运、运输和交付的统一协调。" },
        { title: "市场调研", desc: "在您做出承诺前提供价格、供应和市场情报。" },
        { title: "国际贸易促进", desc: "降低各方、银行与边境之间的交易风险。" },
        { title: "单证支持", desc: "合同、证书和贸易单证的准确制备。" },
      ],
    },
    process: {
      eyebrow: "我们的工作方式",
      title: "从询价到交付的六个严谨步骤。",
      step: "步骤",
      list: [
        { title: "了解买家需求", desc: "我们从您确切的规格、数量和交货条款开始。" },
        { title: "寻找合适的供应商", desc: "我们筛选网络，寻找符合您产品和数量的生产商。" },
        { title: "核实规格", desc: "在报价前确认产品规格、认证和产能。" },
        { title: "商务协调", desc: "协调买卖双方的定价、条款和合同。" },
        { title: "运输与物流支持", desc: "全程的货运、单证和海关协调。" },
        { title: "交付与跟进", desc: "直至最终交付——并进入下一个订单周期。" },
      ],
    },
    why: {
      eyebrow: "为什么选择 Norex",
      title: "合同签署后依然恪守责任的合作伙伴。",
      body: "国际贸易的失败往往源于细节——规格偏差、沟通迟缓、合作方不可靠。Norex 的存在就是为了消除这些失败。我们作为您团队的延伸，在供应链的每一步都坚持质量优先。",
      points: ["全球供应商网络", "多行业专长", "灵活的供应方案", "以质量为核心的理念", "高效沟通", "国际贸易经验", "长期商业关系"],
    },
    buysup: {
      buyers: { label: "致买家", title: "正在寻找可靠的供应来源？", intro: "请发送给我们：", items: ["产品详情", "规格", "数量", "交货目的地"], note: "我们的采购团队将为您寻找合适的供应方案。", cta: "提交询价单" },
      suppliers: { label: "致供应商", title: "拓展您的国际市场版图。", body: "欢迎制造商、矿业公司、生产商、出口商、分销商和供应商与 Norex International Trading Ltd. 合作，拓展国际市场。", cta: "成为供应商" },
    },
    rfq: {
      eyebrow: "询价申请",
      title: "告诉我们您的需求，我们为您寻找供应商。",
      labels: { fullName: "姓名 *", company: "公司名称 *", email: "电子邮箱 *", phone: "电话号码", country: "国家 *", product: "所需产品 *", quantity: "数量", destination: "目的港 / 国家", terms: "首选交货条款", specs: "产品规格", additional: "补充信息", upload: "上传文件（可选，最大10MB）" },
      placeholders: { fullName: "您的姓名", company: "您的公司", email: "you@company.com", phone: "+234 ...", country: "您的国家", product: "例如：AGO柴油、铁矿石", quantity: "例如：50,000 公吨/月", destination: "例如：拉各斯阿帕帕港", terms: "选择国际贸易术语", specs: "等级、标准、包装、认证……", additional: "时间要求、目标价格、其他需要说明的信息……", upload: "规格书、采购意向书或产品文件" },
      terms: ["EXW", "FOB", "CFR", "CIF", "DAP", "DDP", "其他 / 面议"],
      submit: "提交询价单", submitting: "提交中……",
      success: "询价单已提交——我们的采购团队将尽快与您联系。",
      error: "提交失败，请重试。",
      fileTooBig: "文件超过10MB限制",
      nextTitle: "接下来的流程",
      nextSteps: ["我们的采购团队将在24–48小时内审核您的需求。", "我们从供应商网络中识别并核实合适的供应商。", "您将收到包含价格和条款的供应方案。"],
      resp1: "24–48小时内回复", resp2: "您的询价将严格保密",
      whatsapp: "更想直接聊？WhatsApp +234 906 804 3369",
    },
    supplier: {
      eyebrow: "与 Norex 合作",
      title: "您的产品。我们的买家。全球市场。",
      body: "欢迎制造商、矿业公司、生产商、出口商、分销商和供应商与 Norex International Trading Ltd. 合作，拓展国际市场。",
      bullets: ["触达合格的国际买家", "结构化、透明的贸易流程", "出口单证与物流支持", "长期包销与分销合作"],
      labels: { company: "公司名称 *", contact: "联系人 *", email: "电子邮箱 *", phone: "电话号码", country: "国家 *", products: "提供的产品 / 类别 *", message: "留言" },
      placeholders: { company: "您的公司", contact: "姓名", email: "you@company.com", phone: "+234 ...", country: "所在国家", products: "例如：原油、芝麻、医疗耗材", message: "产能、认证、出口经验……" },
      submit: "成为供应商", submitting: "提交中……",
      success: "申请已收到——我们的合作团队将与您联系。",
      error: "提交失败，请重试。",
    },
    testimonials: {
      eyebrow: "客户之声",
      title: "跨越国界与行业的信任。",
      partnersLabel: "我们的客户与贸易伙伴",
      verifiedBadge: "Norex 认证",
      fallback: [
        { quote: "Norex 协调了我们的石油供应，从规格检查到港口卸货——没有任何延误。", name: "Adewale Johnson", org: "Sahel Energy Ltd — 尼日利亚" },
        { quote: "他们的供应商审核为我们节省了数月的尽职调查时间。现在我们通过一个可靠渠道采购医疗耗材。", name: "Fatima Al-Rashid", org: "GulfMed Healthcare Group — 阿联酋" },
        { quote: "每一步都沟通透明。大宗商品贸易本应如此。", name: "Daniel Mwangi", org: "EastAfrica Agro Exports — 肯尼亚" },
        { quote: "从采购意向书到交付，单证支持无可挑剔。", name: "Sophie Laurent", org: "NordIndustrial SARL — 法国" },
        { quote: "一座可靠的桥梁，将我们的矿山与直接无法触达的合格买家连接起来。", name: "Thabo Nkosi", org: "Copperbelt Mining Co — 南非" },
      ],
      partnerNames: ["SAHEL ENERGY", "GOLDCREST MINES", "MEDIPHARM GROUP", "ATLAS AGRO", "NORDWEG TRADE", "KANO TEXTILES"],
    },
    reach: {
      eyebrow: "全球网络",
      title: "横跨五大区域的贸易路线。",
      sub: "连接国际市场上的生产商、供应商、制造商和买家。",
      regions: ["非洲", "中东", "亚洲", "欧洲", "美洲"],
    },
    contact: {
      eyebrow: "联系我们",
      title: "让我们谈谈贸易。",
      officeLabel: "总部", phoneLabel: "电话", emailLabel: "邮箱",
      emailBtn: "发送邮件", whatsappBtn: "WhatsApp 洽谈", quoteBtn: "获取报价",
      cardEyebrow: "Norex International Trading Ltd.",
      cardTitle: "一次询价，开启您的下一条供应线。",
    },
    footer: {
      blurb: "一家国际化的采购、供应、贸易、进出口、分销与供应公司，在全球市场连接可靠的生产商与合格买家。",
      quickTitle: "快速链接", legalTitle: "法律信息", officeTitle: "总部",
      links: { home: "首页", about: "关于我们", industries: "行业领域", services: "服务", rfq: "致买家", suppliers: "致供应商", contact: "联系我们", insights: "市场洞察" },
      legal: { privacy: "隐私政策", terms: "条款与条件", disclaimer: "免责声明" },
      copyright: "© 2026 Norex International Trading Ltd. 版权所有。",
    },
    commodity: {
      back: "返回行业", overview: "Norex 的服务", specs: "典型规格与贸易条款",
      regions: "主要采购地区", formTitle: "获取报价",
      formSub: "请提供数量、目的地和条款——我们的采购团队将在24–48小时内回复。",
      product: "商品", notFound: "未找到该商品。", msgLabel: "留言 / 规格",
      msgPlaceholder: "等级、标准、包装、认证、时间要求……",
    },
    insights: {
      eyebrow: "市场洞察",
      title: "关于全球贸易与大宗商品的观察。",
      sub: "来自 Norex 贸易团队的市场观察与采购情报。",
      readMore: "阅读文章", back: "返回洞察", empty: "暂无已发布的洞察——敬请期待。",
    },
    float: { tooltip: "通过 WhatsApp 与我们联系" },
  },
};

const LangCtx = createContext({ lang: "en", setLang: () => {}, t: translations.en });

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem("norex_lang") || "en");
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);
  const setLang = (l) => {
    localStorage.setItem("norex_lang", l);
    setLangState(l);
  };
  return (
    <LangCtx.Provider value={{ lang, setLang, t: translations[lang] }}>
      {children}
    </LangCtx.Provider>
  );
};

export const useLang = () => useContext(LangCtx);

export const LangToggle = ({ compact = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="lang-toggle" className={`inline-flex items-center border border-white/20 rounded-full overflow-hidden ${compact ? "" : "mr-1"}`}>
      {["en", "fr", "ar", "zh"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`px-2 sm:px-3 py-1.5 text-[10px] sm:text-xs font-semibold uppercase tracking-wider transition-colors duration-200 ${
            lang === l ? "bg-gold text-midnight" : "text-[#94A3B8] hover:text-[#F4F6F8]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};
